/**
 * Meta de cada etapa del ciclo de vida del presupuesto (campo `stage` del backend).
 * Cubre las 8 etapas compuestas: estado de presupuesto + estado de ODT.
 * Solo describe la etapa (descripción y color). Las acciones por etapa viven en
 * `accionesPresupuesto.js`.
 */
export const stageMeta = {
  esperando_aprobar: {
    key: 'esperando_aprobar',
    description: 'Esperando Aprobar',
    color: 'yellow',
  },
  en_espera: {
    key: 'en_espera',
    description: 'En espera',
    color: 'gold',
  },
  rechazado: {
    key: 'rechazado',
    description: 'Rechazado',
    color: 'red',
  },
  aprobado_sin_odt: {
    key: 'aprobado_sin_odt',
    description: 'Aprobado',
    color: 'green',
  },
  odt_no_asignada: {
    key: 'odt_no_asignada',
    description: 'Generada',
    color: 'yellow',
  },
  odt_asignada: {
    key: 'odt_asignada',
    description: 'Orden de Trabajo',
    color: 'orange',
  },
  odt_en_proceso: {
    key: 'odt_en_proceso',
    description: 'Orden Iniciada',
    color: 'blue',
  },
  finalizada: {
    key: 'finalizada',
    description: 'Orden Finalizada',
    color: 'green',
  },
};

/**
 * Meta de los 4 estados puros del presupuesto (budget.status).
 * Para contextos donde solo interesa el estado del documento, sin la etapa compuesta.
 */
export const budgetStatesMeta = {
  esperando_aprobar: { description: 'Esperando Aprobar', color: 'yellow' },
  en_espera: { description: 'En espera', color: 'gold' },
  aprobado: { description: 'Aprobado', color: 'green' },
  rechazado: { description: 'Rechazado', color: 'red' },
};

/**
 * Overlays ortogonales a la etapa (Fase 2). "Vencido" (vigencia pasada) e
 * "Histórico" (revisión reemplazada por una más nueva) NO son estados de la
 * máquina — se derivan en backend (campos `expired`/`superseded`/`revision`) y
 * se muestran como badges junto al tag de etapa. Devuelve [] si no aplica ninguno.
 */
export const budgetOverlayBadges = (row) => {
  const badges = [];
  if (row?.superseded) {
    badges.push({
      key: 'historico',
      text: `Histórico v${row.revision ?? ''}`.trim(),
      color: 'default',
    });
  }
  if (row?.expired) {
    badges.push({ key: 'vencido', text: 'Vencido', color: 'red' });
  }
  return badges;
};

/**
 * Agrupaciones usadas en el KPI strip.
 * Campo principal: `stages` (etapas del ciclo — source of truth).
 * Campo `statuses`: alias de compatibilidad para BudgetKpiStrip/Budget.jsx
 * mientras se migran a `stages` (Tasks 10-11).
 */
export const KPI_GROUPS = [
  {
    key: 'pending',
    label: 'Esperando aprobar',
    stages: ['esperando_aprobar'],
    statuses: ['esperando_aprobar'],
  },
  {
    key: 'approved',
    label: 'Aprobados sin ODT',
    stages: ['aprobado_sin_odt'],
    statuses: ['aprobado'],
  },
  {
    key: 'in_progress',
    label: 'ODT en proceso',
    stages: ['odt_no_asignada', 'odt_asignada', 'odt_en_proceso'],
    statuses: ['odt_no_asignada', 'odt_asignada', 'odt_en_proceso'],
  },
  {
    key: 'done',
    label: 'Finalizadas',
    stages: ['finalizada'],
    statuses: ['odt_finalizada'],
  },
];

/**
 * Devuelve la etapa del ciclo de un row de presupuesto.
 * Centraliza el acceso a `row.stage` para que los consumidores no lean el campo crudo.
 *
 * @param {{ stage: string }} row
 * @returns {string}
 */
export function stageOf(row) {
  return row.stage;
}

/**
 * @deprecated Usar `stageMeta` indexado por `stageOf(row)`.
 * Alias de compatibilidad para BudgetRowActions, BudgetApprovalModal, whitDoc y
 * BudgetToolbar mientras se migran a `stageMeta` (Tasks 10-11).
 *
 * Mapea las claves antiguas (budget.status) a la meta de la etapa equivalente.
 */
export const coloresEstados = {
  odt_no_asignada: { ...stageMeta.odt_no_asignada, key: 'odt_no_asignada' },
  odt_asignada: { ...stageMeta.odt_asignada, key: 'odt_asignada' },
  odt_en_proceso: { ...stageMeta.odt_en_proceso, key: 'odt_en_proceso' },
  odt_finalizada: { ...stageMeta.finalizada, key: 'odt_finalizada' },
  esperando_aprobar: { ...stageMeta.esperando_aprobar, key: 'esperando_aprobar' },
  en_espera: { ...stageMeta.en_espera, key: 'en_espera' },
  rechazado: { ...stageMeta.rechazado, key: 'rechazado' },
  aprobado: { ...stageMeta.aprobado_sin_odt, key: 'aprobado' },
};
